import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  RefreshCw, Camera, Share2, TrendingUp, TrendingDown, Megaphone,
  Eye, Heart, Users, MousePointerClick, Target, DollarSign, Zap, Info, Sparkles,
  Music2, PlayCircle, Hash, Briefcase, MapPin, Play, Star, Phone,
  FileDown, Grid, SplitSquareHorizontal, Crosshair
} from 'lucide-react';
import api from '../api';
import { generateAnalyticsPDF } from '../utils/exportAnalyticsPDF';

/* ════════════════════════════════════════════════════════════════════════
   MARKETING ANALYTICS — multi-piattaforma, data-driven.
   I canali (tab) sono generati dinamicamente dai dati presenti per il cliente.
   Account = clienti attivi. Fonte: /api/meta_analytics/dashboard.
   ════════════════════════════════════════════════════════════════════════ */

const IG = '#DD2A7B', IG2 = '#F58529', IG3 = '#8134AF';
const FB = '#1877F2';
const CY = '#00D4FF', GR = '#10B981', AM = '#F59E0B', PU = '#A855F7', RD = '#EF4444';

/* ── formatters ───────────────────────────────────────────────────────── */
const nf = new Intl.NumberFormat('it-IT');
const cf = new Intl.NumberFormat('it-IT', { notation:'compact', maximumFractionDigits:1 });
const fInt = n => nf.format(Math.round(n||0));
const fK   = n => cf.format(n||0);
const fEur = n => '€ ' + nf.format(Math.round((n||0)*100)/100);
const fEurK= n => '€ ' + cf.format(n||0);
const fPct = n => (Math.round((n||0)*100)/100) + '%';
const fX   = n => (Math.round((n||0)*100)/100) + '×';

const sumF = (ts, f) => ts.reduce((s,r)=>s+(Number(r[f])||0), 0);
const avgF = (ts, f) => ts.length ? ts.reduce((s,r)=>s+(Number(r[f])||0),0)/ts.length : 0;
const deltaPct = (a, b) => { a=Number(a)||0; b=Number(b)||0; if(!b) return a>0?100:0; return (a-b)/Math.abs(b)*100; };

/* ── PLATFORM CONFIG ──────────────────────────────────────────────────── */
const SRC = {
  instagram: { label:'Instagram', color:IG, color2:IG3, icon:Camera, foll:'followers_count', follLabel:'Follower',
    kpis:[['followers_count','Follower',Users,'delta'],['reach_1d','Reach',Eye,'sum'],['profile_views_1d','Visite profilo',MousePointerClick,'sum'],['total_interactions_1d','Interazioni',Heart,'sum']],
    charts:[['followers_count','area','Crescita follower',fInt],['reach_1d','bars','Reach giornaliera',fInt],['total_interactions_1d','area','Interazioni',fInt]],
    postMetric:['reach','Reach'] },
  facebook: { label:'Facebook', color:FB, icon:Share2, foll:'followers_count', follLabel:'Follower',
    kpis:[['followers_count','Follower / Fan',Users,'delta'],['reach_1d','Reach',Eye,'sum'],['total_interactions_1d','Interazioni',Heart,'sum']],
    charts:[['followers_count','area','Crescita follower',fInt],['reach_1d','bars','Reach giornaliera',fInt],['total_interactions_1d','area','Interazioni',fInt]] },
  tiktok: { label:'TikTok', color:'#FE2C55', color2:'#25F4EE', icon:Music2, foll:'followers_count', follLabel:'Follower',
    kpis:[['followers_count','Follower',Users,'delta'],['video_views_1d','Video views',Eye,'sum'],['likes_1d','Like',Heart,'sum'],['shares_1d','Condivisioni',Share2,'sum']],
    charts:[['followers_count','area','Crescita follower',fInt],['video_views_1d','bars','Video views',fInt],['likes_1d','area','Like',fInt]],
    postMetric:['views','Views'] },
  youtube: { label:'YouTube', color:'#FF0000', icon:PlayCircle, foll:'subscribers', follLabel:'Iscritti',
    kpis:[['subscribers','Iscritti',Users,'delta'],['views_1d','Views',Eye,'sum'],['watch_time_min_1d','Minuti visti',Play,'sum'],['likes_1d','Like',Heart,'sum']],
    charts:[['subscribers','area','Crescita iscritti',fInt],['views_1d','bars','Views',fInt],['watch_time_min_1d','area','Minuti di visione',fInt]],
    postMetric:['views','Views'] },
  twitter: { label:'X', color:'#1D9BF0', icon:Hash, foll:'followers_count', follLabel:'Follower',
    kpis:[['followers_count','Follower',Users,'delta'],['impressions_1d','Impression',Eye,'sum'],['engagements_1d','Engagement',Zap,'sum'],['likes_1d','Like',Heart,'sum']],
    charts:[['followers_count','area','Crescita follower',fInt],['impressions_1d','bars','Impression',fInt],['engagements_1d','area','Engagement',fInt]] },
  linkedin: { label:'LinkedIn', color:'#0A66C2', icon:Briefcase, foll:'followers_count', follLabel:'Follower',
    kpis:[['followers_count','Follower',Users,'delta'],['impressions_1d','Impression',Eye,'sum'],['reactions_1d','Reazioni',Heart,'sum'],['shares_1d','Condivisioni',Share2,'sum']],
    charts:[['followers_count','area','Crescita follower',fInt],['impressions_1d','bars','Impression',fInt],['reactions_1d','area','Reazioni',fInt]] },
  google_business: { label:'Google Business', color:'#4285F4', icon:MapPin, foll:null,
    kpis:[['views_1d','Visualizzazioni',Eye,'sum'],['calls_1d','Chiamate',Phone,'sum'],['website_clicks_1d','Click sito',MousePointerClick,'sum'],['rating','Valutazione',Star,'val']],
    charts:[['views_1d','area','Visualizzazioni profilo',fInt],['searches_1d','bars','Ricerche',fInt],['direction_requests_1d','area','Richieste indicazioni',fInt]] },
  ads: { label:'Meta Ads', color:CY, icon:Megaphone },
};
const cfgOf = s => SRC[s] || { label:s, color:'#94A3B8', icon:Hash };

/* ── width hook ───────────────────────────────────────────────────────── */
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(640);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(entries => { const cw = entries[0].contentRect.width; if (cw>0) setW(cw); });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const buildPath = (pts, smooth=true) => {
  if (!pts.length) return '';
  if (pts.length === 1) return `M${pts[0][0]},${pts[0][1]}`;
  if (!smooth) return 'M' + pts.map(p=>`${p[0]},${p[1]}`).join(' L');
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i=0;i<pts.length-1;i++){ const [x0,y0]=pts[i], [x1,y1]=pts[i+1]; const cx=(x0+x1)/2; d += ` C${cx},${y0} ${cx},${y1} ${x1},${y1}`; }
  return d;
};
const shortDate = iso => { const d = new Date(iso); return d.toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit' }); };

/* ── AREA CHART ───────────────────────────────────────────────────────── */
function AreaChart({ data, field, color, color2, height=190, fmt=fInt, label }) {
  const [ref, w] = useWidth();
  const [hov, setHov] = useState(null);
  const gid = useMemo(()=>'g'+Math.random().toString(36).slice(2,8),[]);
  const padL=46, padR=14, padT=16, padB=26;
  const iw = Math.max(40, w - padL - padR), ih = height - padT - padB;
  const vals = data.map(d=>Number(d[field])||0);
  const min = Math.min(...vals, 0), max = Math.max(...vals, 1), span = max-min || 1;
  const x = i => padL + (data.length<=1 ? iw/2 : (i/(data.length-1))*iw);
  const y = v => padT + ih - ((v-min)/span)*ih;
  const pts = data.map((d,i)=>[x(i), y(Number(d[field])||0)]);
  const line = buildPath(pts);
  const area = pts.length ? `${line} L${pts[pts.length-1][0]},${padT+ih} L${pts[0][0]},${padT+ih} Z` : '';
  const ticks=4;
  const onMove = e => { const r=e.currentTarget.getBoundingClientRect(); const mx=e.clientX-r.left; let b=0,bd=1e9; pts.forEach((p,i)=>{const dd=Math.abs(p[0]-mx); if(dd<bd){bd=dd;b=i;}}); setHov(b); };
  return (
    <div ref={ref} style={{ position:'relative', width:'100%' }}>
      <svg width={w} height={height} style={{ display:'block' }}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.42"/><stop offset="100%" stopColor={color2||color} stopOpacity="0.02"/>
        </linearGradient></defs>
        {Array.from({length:ticks+1}).map((_,i)=>{ const v=min+(span*i/ticks), yy=y(v); return (<g key={i}>
          <line x1={padL} y1={yy} x2={w-padR} y2={yy} stroke="var(--border)" strokeWidth={0.7} opacity={0.5}/>
          <text x={padL-8} y={yy+3} textAnchor="end" fontSize={9.5} fill="var(--text-dim)">{fmt(v)}</text></g>); })}
        <path d={area} fill={`url(#${gid})`}/>
        <path d={line} fill="none" stroke={color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round"/>
        {data.length>0 && [0, Math.floor((data.length-1)/2), data.length-1].filter((v,i,a)=>a.indexOf(v)===i).map(i=>(
          <text key={i} x={x(i)} y={height-8} textAnchor="middle" fontSize={9.5} fill="var(--text-dim)">{shortDate(data[i].date)}</text>))}
        {hov!=null && pts[hov] && (<g>
          <line x1={pts[hov][0]} y1={padT} x2={pts[hov][0]} y2={padT+ih} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.6}/>
          <circle cx={pts[hov][0]} cy={pts[hov][1]} r={4.5} fill={color} stroke="var(--bg2)" strokeWidth={2}/></g>)}
        <rect x={padL} y={padT} width={iw} height={ih} fill="transparent" onMouseMove={onMove} onMouseLeave={()=>setHov(null)}/>
      </svg>
      {hov!=null && data[hov] && (
        <div style={{ position:'absolute', left:Math.min(Math.max(pts[hov][0],60), w-70), top:6, transform:'translateX(-50%)',
          background:'rgba(7,7,26,0.95)', border:`1px solid ${color}`, borderRadius:8, padding:'5px 9px', pointerEvents:'none', whiteSpace:'nowrap' }}>
          <div style={{ fontSize:9.5, color:'var(--text-dim)' }}>{shortDate(data[hov].date)}</div>
          <div style={{ fontSize:12.5, fontWeight:700, color }}>{label}: {fmt(data[hov][field])}</div>
        </div>)}
    </div>
  );
}

/* ── MULTI-LINE ───────────────────────────────────────────────────────── */
function MultiLine({ data, lines, height=210, fmt=fInt }) {
  const [ref, w] = useWidth();
  const [hov, setHov] = useState(null);
  const padL=46, padR=14, padT=16, padB=26;
  const iw = Math.max(40, w - padL - padR), ih = height - padT - padB;
  const all = lines.flatMap(l => data.map(d=>Number(d[l.field])||0));
  const min = Math.min(...all, 0), max = Math.max(...all, 1), span = max-min || 1;
  const x = i => padL + (data.length<=1 ? iw/2 : (i/(data.length-1))*iw);
  const y = v => padT + ih - ((v-min)/span)*ih;
  const ticks=4;
  const onMove = e => { const r=e.currentTarget.getBoundingClientRect(); const mx=e.clientX-r.left; let b=0,bd=1e9; data.forEach((_,i)=>{const dd=Math.abs(x(i)-mx); if(dd<bd){bd=dd;b=i;}}); setHov(b); };
  return (
    <div ref={ref} style={{ position:'relative', width:'100%' }}>
      <svg width={w} height={height} style={{ display:'block' }}>
        {Array.from({length:ticks+1}).map((_,i)=>{ const v=min+(span*i/ticks), yy=y(v); return (<g key={i}>
          <line x1={padL} y1={yy} x2={w-padR} y2={yy} stroke="var(--border)" strokeWidth={0.7} opacity={0.5}/>
          <text x={padL-8} y={yy+3} textAnchor="end" fontSize={9.5} fill="var(--text-dim)">{fmt(v)}</text></g>); })}
        {lines.map(l=>(<path key={l.field} d={buildPath(data.map((d,i)=>[x(i), y(Number(d[l.field])||0)]))}
          fill="none" stroke={l.color} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round"/>))}
        {data.length>0 && [0, Math.floor((data.length-1)/2), data.length-1].filter((v,i,a)=>a.indexOf(v)===i).map(i=>(
          <text key={i} x={x(i)} y={height-8} textAnchor="middle" fontSize={9.5} fill="var(--text-dim)">{shortDate(data[i].date)}</text>))}
        {hov!=null && (<g>
          <line x1={x(hov)} y1={padT} x2={x(hov)} y2={padT+ih} stroke="var(--text-muted)" strokeWidth={1} strokeDasharray="3 3" opacity={0.5}/>
          {lines.map(l=>(<circle key={l.field} cx={x(hov)} cy={y(Number(data[hov][l.field])||0)} r={4} fill={l.color} stroke="var(--bg2)" strokeWidth={2}/>))}</g>)}
        <rect x={padL} y={padT} width={iw} height={ih} fill="transparent" onMouseMove={onMove} onMouseLeave={()=>setHov(null)}/>
      </svg>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', padding:'2px 4px 0 46px' }}>
        {lines.map(l=>(<span key={l.field} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'var(--text-muted)' }}>
          <span style={{ width:10, height:3, borderRadius:2, background:l.color, display:'inline-block' }}/>{l.label}
          {hov!=null && <b style={{ color:l.color }}>· {fmt(data[hov][l.field])}</b>}</span>))}
      </div>
    </div>
  );
}

/* ── BARS ─────────────────────────────────────────────────────────────── */
function Bars({ data, field, color, height=190, fmt=fInt, label }) {
  const [ref, w] = useWidth();
  const [hov, setHov] = useState(null);
  const gid = useMemo(()=>'b'+Math.random().toString(36).slice(2,8),[]);
  const padL=46, padR=14, padT=16, padB=26;
  const iw = Math.max(40, w - padL - padR), ih = height - padT - padB;
  const max = Math.max(...data.map(d=>Number(d[field])||0), 1);
  const n = data.length || 1, bw = Math.max(2, (iw/n)*0.62);
  const x = i => padL + (i+0.5)*(iw/n);
  const y = v => padT + ih - (v/max)*ih;
  const ticks=4;
  return (
    <div ref={ref} style={{ position:'relative', width:'100%' }}>
      <svg width={w} height={height} style={{ display:'block' }}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95"/><stop offset="100%" stopColor={color} stopOpacity="0.35"/></linearGradient></defs>
        {Array.from({length:ticks+1}).map((_,i)=>{ const v=(max*i/ticks), yy=y(v); return (<g key={i}>
          <line x1={padL} y1={yy} x2={w-padR} y2={yy} stroke="var(--border)" strokeWidth={0.7} opacity={0.5}/>
          <text x={padL-8} y={yy+3} textAnchor="end" fontSize={9.5} fill="var(--text-dim)">{fmt(v)}</text></g>); })}
        {data.map((d,i)=>{ const v=Number(d[field])||0, yy=y(v); return <rect key={i} x={x(i)-bw/2} y={yy} width={bw} height={Math.max(0,padT+ih-yy)} rx={2}
          fill={`url(#${gid})`} opacity={hov===i?1:0.85} onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}/>; })}
        {data.length>0 && [0, Math.floor((data.length-1)/2), data.length-1].filter((v,i,a)=>a.indexOf(v)===i).map(i=>(
          <text key={i} x={x(i)} y={height-8} textAnchor="middle" fontSize={9.5} fill="var(--text-dim)">{shortDate(data[i].date)}</text>))}
      </svg>
      {hov!=null && data[hov] && (
        <div style={{ position:'absolute', left:Math.min(Math.max(x(hov),60), w-70), top:6, transform:'translateX(-50%)',
          background:'rgba(7,7,26,0.95)', border:`1px solid ${color}`, borderRadius:8, padding:'5px 9px', pointerEvents:'none', whiteSpace:'nowrap' }}>
          <div style={{ fontSize:9.5, color:'var(--text-dim)' }}>{shortDate(data[hov].date)}</div>
          <div style={{ fontSize:12.5, fontWeight:700, color }}>{label}: {fmt(data[hov][field])}</div>
        </div>)}
    </div>
  );
}

/* ── SPARKLINE ────────────────────────────────────────────────────────── */
function Sparkline({ data, field, color, w=92, h=30 }) {
  const vals = data.map(d=>Number(d[field])||0);
  if (!vals.length) return null;
  const min=Math.min(...vals), max=Math.max(...vals), span=max-min||1;
  const x=i=>(vals.length<=1?w/2:(i/(vals.length-1))*w), y=v=>h-2-((v-min)/span)*(h-4);
  const pts=vals.map((v,i)=>[x(i),y(v)]);
  return (<svg width={w} height={h} style={{ display:'block' }}>
    <path d={`${buildPath(pts)} L${pts[pts.length-1][0]},${h} L${pts[0][0]},${h} Z`} fill={color} opacity={0.13}/>
    <path d={buildPath(pts)} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r={2.4} fill={color}/></svg>);
}

/* ── KPI CARD ─────────────────────────────────────────────────────────── */
function Kpi({ icon:Icon, label, value, delta, color, spark, sparkField, goodWhenUp=true, hint }) {
  const hasDelta = delta != null && isFinite(delta);
  const up = delta >= 0, good = goodWhenUp ? up : !up;
  const dColor = !hasDelta ? 'var(--text-dim)' : (Math.abs(delta)<0.05 ? 'var(--text-muted)' : good ? GR : RD);
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:13, padding:'14px 16px',
      display:'flex', flexDirection:'column', gap:8, minWidth:0, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, width:'100%', height:3, background:color, opacity:0.85 }}/>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7, minWidth:0 }}>
          <div style={{ width:26, height:26, borderRadius:7, background:`${color}1F`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon size={14} color={color}/></div>
          <span style={{ fontSize:11.5, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
        </div>
        {hint && <span title={hint}><Info size={12} color="var(--text-dim)"/></span>}
      </div>
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:8 }}>
        <div style={{ fontSize:23, fontWeight:800, color:'var(--text)', lineHeight:1 }}>{value}</div>
        {spark && sparkField && <Sparkline data={spark} field={sparkField} color={color}/>}
      </div>
      {hasDelta && (
        <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11.5, color:dColor, fontWeight:600 }}>
          {up ? <TrendingUp size={13}/> : <TrendingDown size={13}/>}
          {Math.abs(Math.round(delta*10)/10)}%<span style={{ color:'var(--text-dim)', fontWeight:400 }}>nel periodo</span>
        </div>)}
    </div>
  );
}

function Panel({ title, sub, children, right }) {
  return (
    <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10, marginBottom:12 }}>
        <div><div style={{ fontSize:13.5, fontWeight:700, color:'var(--text)' }}>{title}</div>
          {sub && <div style={{ fontSize:11.5, color:'var(--text-muted)', marginTop:1 }}>{sub}</div>}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
const EmptyChart = ({ msg }) => (
  <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-dim)', fontSize:12.5, textAlign:'center', padding:'0 20px' }}>{msg}</div>
);
const grid = (min='270px') => ({ display:'grid', gridTemplateColumns:`repeat(auto-fit, minmax(${min}, 1fr))`, gap:14 });

/* ── chart from spec ──────────────────────────────────────────────────── */
function ChartFor({ ts, spec, color, color2 }) {
  const [field, type, label, fmt] = spec;
  if (ts.length <= 1) return <EmptyChart msg="Serve più di uno snapshot — il grafico si popola con la raccolta giornaliera."/>;
  return type==='bars'
    ? <Bars data={ts} field={field} color={color} label={label} fmt={fmt||fInt}/>
    : <AreaChart data={ts} field={field} color={color} color2={color2} label={label} fmt={fmt||fInt}/>;
}

/* ── PAGE ─────────────────────────────────────────────────────────────── */
export default function MetaAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(false);
  const [tab, setTab]         = useState('overview');
  const [days, setDays]       = useState(30);
  const [account, setAccount] = useState(null);
  const [posts, setPosts]         = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [compareData, setCompareData]   = useState(null);
  const [compareDays, setCompareDays]   = useState(30);
  const [pdfLoading, setPdfLoading]     = useState(false);
  const [goals, setGoals]         = useState([]);

  const load = useCallback(() => {
    setLoading(true); setErr(false);
    api.get('/meta_analytics/dashboard', { params:{ days, account: account || undefined } })
      .then(r => { setData(r.data); setAccount(r.data.account); setLoading(false); })
      .catch(() => { setErr(true); setLoading(false); });
  }, [days, account]);

  // Sincronizza dati e contenuti: allinea stati/insights da PostProxy e riversa
  // gli insights per-post in meta_analytics, poi ricarica la dashboard.
  const [syncing, setSyncing] = useState(false);
  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await api.post('/meta_analytics/sync');
      window.dispatchEvent(new CustomEvent('eden-toast', { detail: {
        message: `Sincronizzato: ${r.data.stati_aggiornati} stati, ${r.data.post_sincronizzati} contenuti`, type: 'success' } }));
      load();
    } catch { /* toast dall'interceptor */ } finally { setSyncing(false); }
  };

  useEffect(() => { load(); }, [load]);

  const accounts = data?.accounts || [];
  const channels = useMemo(() => data?.channels || [], [data]);
  const curAcct  = accounts.find(a => a.account_key === (data?.account || account));
  const noLink   = data && curAcct && !curAcct.has_data;
  const chMap    = useMemo(() => Object.fromEntries(channels.map(c=>[c.source,c])), [channels]);

  // se la tab attiva non esiste più per questo account, torna al riepilogo
  useEffect(() => {
    if (tab!=='overview' && tab!=='contenuti' && tab!=='confronto' && tab!=='goals' && channels.length && !chMap[tab]) setTab('overview');
  }, [tab, channels, chMap]);

  useEffect(() => {
    if (tab !== 'contenuti' || !account || !data?.channels?.length) return;
    const sources = (data.channels || []).filter(c => c.source !== 'ads').map(c => c.source);
    if (!sources.length) return;
    setPostsLoading(true);
    Promise.all(sources.map(s => api.get('/meta_analytics/posts', { params:{ account_key: account, source: s, days } }).then(r => (r.data.posts || []).map(p => ({...p, _src:s}))).catch(()=>[])))
      .then(results => { setPosts(results.flat()); setPostsLoading(false); })
      .catch(() => setPostsLoading(false));
  }, [tab, account, data, days]);

  useEffect(() => {
    if (tab !== 'goals' || !account) return;
    api.get(`/analytics_goals/${account}`).then(r => setGoals(r.data)).catch(() => {});
  }, [tab, account]);

  const tabs = [
    { key:'overview', label:'Riepilogo', icon:Sparkles },
    ...channels.map(c => ({ key:c.source, label:cfgOf(c.source).label, icon:cfgOf(c.source).icon })),
    { key:'contenuti', label:'Contenuti', icon:Grid },
    { key:'confronto', label:'Confronto', icon:SplitSquareHorizontal },
    { key:'goals', label:'Goals', icon:Crosshair },
  ];

  return (
    <div className="eden-page">
      {/* header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, flexWrap:'wrap', marginBottom:18 }}>
        <div style={{ display:'flex', alignItems:'center', gap:13 }}>
          <div style={{ width:42, height:42, borderRadius:11, flexShrink:0, background:`linear-gradient(135deg, ${IG2}, ${IG}, ${IG3})`,
            display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 16px ${IG}55` }}><TrendingUp size={22} color="#fff"/></div>
          <div>
            <h1 style={{ fontSize:21, fontWeight:800, margin:0, color:'var(--text)' }}>Marketing Analytics</h1>
            <p style={{ fontSize:12.5, color:'var(--text-muted)', margin:0 }}>
              {curAcct ? <><b style={{ color:'var(--text)' }}>{curAcct.label}</b> · social & advertising</> : 'social & advertising'}
            </p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <div style={{ display:'flex', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:9, overflow:'hidden' }}>
            {[7,30,90].map(d=>(<button key={d} onClick={()=>setDays(d)} style={{ padding:'7px 12px', border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
              background: days===d?'var(--cyan-dim)':'transparent', color: days===d?'var(--cyan)':'var(--text-muted)' }}>{d}g</button>))}
          </div>
          <button
            onClick={() => {
              if (!data || !channels.length) return;
              setPdfLoading(true);
              setTimeout(() => {
                try {
                  generateAnalyticsPDF({
                    clienteLabel: curAcct?.label || account || 'Cliente',
                    days,
                    channels,
                  });
                } catch(e) { console.error('PDF error', e); }
                setPdfLoading(false);
              }, 50);
            }}
            disabled={pdfLoading || !data || !channels.length}
            title="Scarica Report PDF"
            style={{ display:'flex', alignItems:'center', gap:5, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:9, padding:'7px 11px', cursor: (pdfLoading || !data) ? 'default' : 'pointer', color: pdfLoading ? 'var(--cyan)' : 'var(--text-muted)', fontSize:12, opacity: (!data || !channels.length) ? 0.4 : 1 }}>
            <FileDown size={14}/> {pdfLoading ? 'Generando…' : 'PDF'}
          </button>
          <button onClick={syncNow} disabled={syncing} title="Sincronizza dati e contenuti da PostProxy"
            style={{ display:'flex', alignItems:'center', gap:6, background:'var(--cyan-dim)', border:'1px solid var(--cyan)', borderRadius:9, padding:'8px 13px', cursor: syncing?'default':'pointer', color:'var(--cyan)', fontSize:12, fontWeight:700, opacity: syncing?0.6:1 }}>
            <RefreshCw size={14} style={ syncing ? { animation:'eden-spin 0.7s linear infinite' } : undefined }/> {syncing ? 'Sincronizzo…' : 'Sincronizza'}
          </button>
          <button onClick={load} title="Ricarica dashboard" style={{ display:'flex', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:9, padding:8, cursor:'pointer', color:'var(--text-muted)' }}><RefreshCw size={15}/></button>
        </div>
      </div>

      {/* account selector = clienti attivi */}
      {accounts.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <span style={{ fontSize:11, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'.05em' }}>Cliente attivo</span>
            <span style={{ fontSize:11, color:'var(--text-dim)' }}>· {accounts.length} {accounts.length===1?'cliente':'clienti'}</span>
          </div>
          <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:4 }}>
            {accounts.map(a => {
              const sel = a.account_key === account;
              return (
                <button key={a.account_key} onClick={()=>setAccount(a.account_key)} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:12, cursor:'pointer',
                  flexShrink:0, textAlign:'left', transition:'all .15s', border:`1px solid ${sel?IG:'var(--border)'}`, background: sel?`${IG}14`:'var(--surface)', boxShadow: sel?`0 0 0 1px ${IG}`:'none' }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', flexShrink:0, overflow:'hidden', color:'#fff',
                    background: a.logo_url ? 'var(--surface)' : `linear-gradient(135deg, ${IG2}, ${IG}, ${IG3})`,
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800 }}>
                    {a.logo_url
                      ? <img src={a.logo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      : (a.label||'?').trim().charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color: sel?'var(--text)':'var(--text-muted)', whiteSpace:'nowrap' }}>{a.label}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
                      {a.has_data ? (
                        <span style={{ display:'flex', gap:4, alignItems:'center' }}>
                          {(a.channels||[]).map(s=>(<span key={s} title={cfgOf(s).label} style={{ width:7, height:7, borderRadius:'50%', background:cfgOf(s).color }}/>))}
                          <span style={{ fontSize:10, color:'var(--text-dim)', marginLeft:2 }}>{(a.channels||[]).length} canali</span>
                        </span>
                      ) : (<span style={{ fontSize:10.5, color:'var(--text-dim)', fontStyle:'italic' }}>nessun account collegato</span>)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* banner dati */}
      {data && !noLink && (
        <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--green-dim)', border:'1px solid rgba(16,185,129,0.3)', borderRadius:11, padding:'10px 14px', marginBottom:16, fontSize:12.5, color:'var(--text-muted)' }}>
          <Info size={16} color={GR} style={{ flexShrink:0 }}/>
          <div><b style={{ color:GR }}>Dati reali</b> · {data.real_days} giorno/i di snapshot. Le tab si arricchiscono ad ogni esecuzione del playbook 8.1.</div>
        </div>)}
      {noLink && (
        <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--surface)', border:'1px dashed var(--border)', borderRadius:11, padding:'14px 16px', marginBottom:16, fontSize:13, color:'var(--text-muted)' }}>
          <Info size={16} style={{ flexShrink:0 }} color="var(--text-dim)"/>
          <div><b style={{ color:'var(--text)' }}>{curAcct?.label}</b> non ha ancora account social/ads collegati. Collega i profili (IG, FB, TikTok, YouTube, X, LinkedIn, Google Business) via PostProxy ed esegui il playbook <b style={{ color:'var(--text)' }}>8.1</b>.</div>
        </div>)}

      {/* tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
        {tabs.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 15px', borderRadius:10, cursor:'pointer',
            border:'1px solid', fontSize:13, fontWeight:600, borderColor: tab===t.key?'transparent':'var(--border)',
            background: tab===t.key?(t.key==='overview'?'var(--cyan)':cfgOf(t.key).color):'var(--surface)',
            color: tab===t.key?'#fff':'var(--text-muted)' }}>
            <t.icon size={15}/> {t.label}
          </button>))}
      </div>

      {loading && <div style={{ padding:80, textAlign:'center', color:'var(--text-dim)', fontSize:14 }}>Caricamento analytics…</div>}
      {err && <div style={{ padding:60, textAlign:'center', color:RD, fontSize:14 }}>Errore nel caricamento dei dati.</div>}

      {data && !loading && !err && (
        tab==='overview' ? <Overview channels={channels} chMap={chMap} posts={data.posts} days={days}/>
        : tab==='contenuti' ? <ContenutiTab posts={posts} loading={postsLoading} cfgOf={cfgOf} fK={fK} fInt={fInt}/>
        : tab==='confronto' ? <ConfrontoTab account={account} channels={channels} days={days} compareDays={compareDays} setCompareDays={setCompareDays} cfgOf={cfgOf} fInt={fInt}/>
        : tab==='goals' ? <GoalsTab account={account} goals={goals} setGoals={setGoals} channels={channels} cfgOf={cfgOf}/>
        : (chMap[tab]
            ? (tab==='ads' ? <AdsTab ch={chMap.ads}/> : <SocialTab ch={chMap[tab]} posts={data.posts?.[tab]} days={days}/>)
            : <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Nessun dato per questo canale.</div>)
      )}
    </div>
  );
}

/* ── OVERVIEW ─────────────────────────────────────────────────────────── */
function Overview({ channels, chMap, posts, days }) {
  if (!channels.length) return <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:14 }}>Nessun canale collegato per questo cliente.</div>;

  // KPI: una card per canale (metrica primaria)
  const kpis = channels.map(c => {
    const cfg = cfgOf(c.source);
    if (c.source === 'ads') return { key:'ads', label:'Spesa Ads (periodo)', value:fEurK(sumF(c.timeseries,'spend')), color:CY, icon:Megaphone, spark:c.timeseries, sparkField:'spend', goodWhenUp:false };
    if (cfg.foll) return { key:c.source, label:`${cfg.label} · ${cfg.follLabel}`, value:fInt(c.latest[cfg.foll]), delta:deltaPct(c.latest[cfg.foll], c.first[cfg.foll]), color:cfg.color, icon:cfg.icon, spark:c.timeseries, sparkField:cfg.foll };
    return { key:c.source, label:`${cfg.label} · Visualizzazioni`, value:fK(sumF(c.timeseries,'views_1d')), color:cfg.color, icon:cfg.icon, spark:c.timeseries, sparkField:'views_1d' };
  });

  // multi-linea: follower/iscritti dei canali che li hanno
  const follCh = channels.filter(c => cfgOf(c.source).foll && c.timeseries.length>1);
  let merged = [], lines = [];
  if (follCh.length) {
    const base = follCh.reduce((a,b)=>b.timeseries.length>a.timeseries.length?b:a).timeseries;
    merged = base.map((row, i) => {
      const o = { date: row.date };
      follCh.forEach(c => { const f=cfgOf(c.source).foll; const rec=c.timeseries.find(r=>r.date===row.date)||c.timeseries[i]||{}; o[c.source]=Number(rec[f])||0; });
      return o;
    });
    lines = follCh.map(c => ({ field:c.source, label:cfgOf(c.source).label, color:cfgOf(c.source).color }));
  }

  const ads = chMap.ads;
  const allPosts = posts ? Object.entries(posts).flatMap(([src,arr]) => arr.map(p=>({...p, _src:src}))) : [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={grid('230px')}>{kpis.map(k=><Kpi key={k.key} {...k}/>)}</div>

      <Panel title="Crescita pubblico" sub={`Follower / iscritti per canale · ${days} giorni`}>
        {merged.length>1 ? <MultiLine data={merged} lines={lines} height={230}/> : <EmptyChart msg="Servono più snapshot per il confronto tra canali."/>}
      </Panel>

      {ads && (
        <Panel title="Spesa pubblicitaria" sub="Meta Ads · spesa giornaliera (€)">
          {ads.timeseries.length>1 ? <AreaChart data={ads.timeseries} field="spend" color={CY} label="Spesa" fmt={fEur}/> : <EmptyChart msg="Nessuna spesa nel periodo."/>}
        </Panel>)}

      {ads?.campaigns?.length>0 && <CampaignsPanel campaigns={ads.campaigns}/>}
      {allPosts.length>0 && <PostsPanel posts={allPosts} title="Top contenuti" showSource/>}
    </div>
  );
}

/* ── SOCIAL TAB (generica) ────────────────────────────────────────────── */
function SocialTab({ ch, posts, days }) {
  const cfg = cfgOf(ch.source);
  const ts = ch.timeseries, latest = ch.latest, first = ch.first;
  const [c0, ...rest] = cfg.charts || [];
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={grid('230px')}>
        {(cfg.kpis||[]).map(([field,label,Icon,mode])=>{
          if (mode==='val') return <Kpi key={field} icon={Icon} label={label} color={cfg.color} value={`${latest[field]??'—'}${field==='rating'?'★':''}`}/>;
          if (mode==='delta') return <Kpi key={field} icon={Icon} label={label} color={cfg.color} value={fInt(latest[field])} delta={deltaPct(latest[field], first[field])} spark={ts} sparkField={field}/>;
          // sum
          return <Kpi key={field} icon={Icon} label={`${label} (periodo)`} color={cfg.color} value={fK(sumF(ts,field))} spark={ts} sparkField={field}/>;
        })}
      </div>
      {c0 && <Panel title={c0[2]} sub={`${cfg.label} · ${days} giorni`}><ChartFor ts={ts} spec={c0} color={cfg.color} color2={cfg.color2}/></Panel>}
      {rest.length>0 && (
        <div style={grid('360px')}>
          {rest.map((spec,i)=>(<Panel key={i} title={spec[2]}><ChartFor ts={ts} spec={spec} color={cfg.color} color2={cfg.color2}/></Panel>))}
        </div>)}
      {cfg.postMetric && posts?.length>0 && <PostsPanel posts={posts} title={`Top contenuti ${cfg.label}`} metric={cfg.postMetric} color={cfg.color}/>}
    </div>
  );
}

/* ── ADS TAB ──────────────────────────────────────────────────────────── */
function AdsTab({ ch }) {
  const ts = ch.timeseries;
  const noData = sumF(ts,'impressions')===0;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={grid('220px')}>
        <Kpi icon={DollarSign} label="Spesa (periodo)" color={CY} value={fEurK(sumF(ts,'spend'))} spark={ts} sparkField="spend" goodWhenUp={false}/>
        <Kpi icon={Eye} label="Impression" color={PU} value={fK(sumF(ts,'impressions'))} spark={ts} sparkField="impressions"/>
        <Kpi icon={MousePointerClick} label="Click" color={IG2} value={fK(sumF(ts,'clicks'))} spark={ts} sparkField="clicks"/>
        <Kpi icon={Target} label="Conversioni" color={GR} value={fInt(sumF(ts,'conversions'))} spark={ts} sparkField="conversions"/>
        <Kpi icon={Zap} label="CTR medio" color="#0EA5E9" value={fPct(avgF(ts,'ctr'))} spark={ts} sparkField="ctr"/>
        <Kpi icon={TrendingUp} label="ROAS medio" color={AM} value={fX(avgF(ts,'roas'))} spark={ts} sparkField="roas"/>
        <Kpi icon={DollarSign} label="CPC medio" color="#94A3B8" value={fEur(avgF(ts,'cpc'))} goodWhenUp={false}/>
        <Kpi icon={DollarSign} label="CPM medio" color="#94A3B8" value={fEur(avgF(ts,'cpm'))} goodWhenUp={false}/>
      </div>
      {noData && (
        <div style={{ background:'var(--surface)', border:'1px dashed var(--border)', borderRadius:12, padding:'18px', fontSize:13, color:'var(--text-muted)' }}>
          {ch.latest?.note || 'Nessuna campagna attiva al momento.'} Collega un metodo di pagamento e avvia una campagna per popolare i grafici.
        </div>)}
      {!noData && (<>
        <div style={grid('360px')}>
          <Panel title="Spesa giornaliera" sub="Meta Ads (€)"><ChartFor ts={ts} spec={['spend','area','Spesa',fEur]} color={CY}/></Panel>
          <Panel title="Impression giornaliere"><ChartFor ts={ts} spec={['impressions','bars','Impression',fInt]} color={PU}/></Panel>
          <Panel title="ROAS" sub="Ritorno sulla spesa"><ChartFor ts={ts} spec={['roas','area','ROAS',fX]} color={AM}/></Panel>
          <Panel title="CTR" sub="Click-through rate"><ChartFor ts={ts} spec={['ctr','area','CTR',fPct]} color="#0EA5E9"/></Panel>
        </div>
        {ch.campaigns?.length>0 && <CampaignsPanel campaigns={ch.campaigns}/>}
      </>)}
    </div>
  );
}

/* ── CAMPAIGNS ────────────────────────────────────────────────────────── */
const tdNum = { padding:'9px 10px', textAlign:'right', color:'var(--text-muted)', whiteSpace:'nowrap' };
function CampaignsPanel({ campaigns }) {
  const c = campaigns || [];
  if (!c.length) return null;
  const maxSpend = Math.max(...c.map(x=>x.spend||0), 1);
  return (
    <Panel title="Campagne attive" sub={`${c.length} campagne · ordinate per spesa`}>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5, minWidth:560 }}>
          <thead><tr style={{ color:'var(--text-dim)', fontSize:10.5, textTransform:'uppercase', letterSpacing:'.04em' }}>
            {['Campagna','Spesa','Impression','Click','CTR','Conv.','ROAS'].map((h,i)=>(<th key={h} style={{ textAlign:i===0?'left':'right', padding:'6px 10px', fontWeight:700, borderBottom:'1px solid var(--border)' }}>{h}</th>))}
          </tr></thead>
          <tbody>{c.map((r,i)=>(
            <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
              <td style={{ padding:'9px 10px' }}>
                <div style={{ fontWeight:600, color:'var(--text)' }}>{r.nome}</div>
                {r.objective && <div style={{ fontSize:10.5, color:'var(--text-dim)' }}>{r.objective}</div>}
                <div style={{ height:4, borderRadius:3, background:'var(--border)', marginTop:5, overflow:'hidden', maxWidth:200 }}>
                  <div style={{ height:'100%', width:`${(r.spend/maxSpend)*100}%`, background:CY, borderRadius:3 }}/></div>
              </td>
              <td style={tdNum}>{fEur(r.spend)}</td><td style={tdNum}>{fK(r.impressions)}</td><td style={tdNum}>{fK(r.clicks)}</td>
              <td style={tdNum}>{fPct(r.ctr)}</td><td style={tdNum}>{fInt(r.conversions)}</td>
              <td style={{...tdNum, color: r.roas>=2?GR:r.roas>=1?AM:RD, fontWeight:700 }}>{fX(r.roas)}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ── TOP POSTS ────────────────────────────────────────────────────────── */
const TYPE_LABEL = { reel:'Reel', carousel:'Carosello', image:'Foto', video:'Video', short:'Short' };
// Metrica di riferimento di un contenuto: la prima davvero presente nei dati.
// Gli insights dei post spesso hanno SOLO impressions: cercare solo reach/views
// faceva mostrare 0 su ogni contenuto (e ordinarli a caso).
const POST_METRICS = [['reach','Reach'], ['impressions','Impressions'], ['views','Views'], ['likes','Like']];
const postVal = (p, pref) => {
  for (const [k] of (pref ? [[pref]] : []).concat(POST_METRICS)) {
    const v = Number(p?.[k] || 0);
    if (v) return v;
  }
  return 0;
};

function PostsPanel({ posts, title='Top contenuti', metric=null, color=IG, showSource=false }) {
  if (!posts || !posts.length) return null;
  const pref = metric ? metric[0] : null;
  // etichetta = la prima metrica realmente valorizzata sui contenuti mostrati
  const found = POST_METRICS.find(([k]) => posts.some(p => Number(p?.[k] || 0)));
  const label = (metric && posts.some(p => Number(p?.[pref] || 0))) ? metric[1] : (found ? found[1] : 'Reach');
  const sorted = [...posts].sort((a,b)=>postVal(b,pref)-postVal(a,pref)).slice(0,8);
  const maxV = Math.max(...sorted.map(p=>postVal(p,pref)), 1);
  return (
    <Panel title={title} sub={`${sorted.length} contenuti · per ${label.toLowerCase()}`}>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {sorted.map((p,i)=>{
          const v = postVal(p, pref);
          const c = showSource ? cfgOf(p._src).color : color;
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 13px' }}>
              <div style={{ width:30, height:30, borderRadius:8, flexShrink:0, background:`${c}1F`, color:c, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800 }}>#{i+1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.titolo}</div>
                <div style={{ display:'flex', gap:12, marginTop:3, fontSize:11, color:'var(--text-muted)', flexWrap:'wrap' }}>
                  {showSource && <span style={{ color:c, fontWeight:600 }}>{cfgOf(p._src).label}</span>}
                  {p.type && <span style={{ color:c, fontWeight:600 }}>{TYPE_LABEL[p.type]||p.type}</span>}
                  <span><Eye size={11} style={{ verticalAlign:'-1px' }}/> {fK(v)}</span>
                  {p.likes!=null && <span><Heart size={11} style={{ verticalAlign:'-1px' }}/> {fK(p.likes)}</span>}
                  {p.comments!=null && <span>💬 {fInt(p.comments)}</span>}
                  {p.saves!=null && <span>🔖 {fInt(p.saves)}</span>}
                  {p.engagement_rate!=null && <span style={{ color:GR, fontWeight:600 }}>{fPct(p.engagement_rate)} eng.</span>}
                </div>
                <div style={{ height:4, borderRadius:3, background:'var(--border)', marginTop:6, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(v/maxV)*100}%`, background:c, borderRadius:3 }}/></div>
              </div>
            </div>);
        })}
      </div>
    </Panel>
  );
}

/* ── CONTENUTI TAB ────────────────────────────────────────────────────── */
function ContenutiTab({ posts, loading, cfgOf, fK, fInt }) {
  const [filtroSource, setFiltroSource] = useState('');
  const sources = [...new Set(posts.map(p => p._src))];
  const filtered = filtroSource ? posts.filter(p => p._src === filtroSource) : posts;
  const sorted = [...filtered].sort((a,b) => {
    const va = Number(a.metrics?.reach || a.metrics?.impressions || a.metrics?.views || a.metrics?.video_views || 0);
    const vb = Number(b.metrics?.reach || b.metrics?.impressions || b.metrics?.views || b.metrics?.video_views || 0);
    return vb - va;
  }).slice(0, 30);

  if (loading) return <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)' }}>Caricamento contenuti…</div>;
  if (!posts.length) return (
    <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
      Nessun contenuto disponibile. I post vengono importati con la raccolta giornaliera.
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {['', ...sources].map(s => {
          const cfg = s ? cfgOf(s) : null;
          return (
            <button key={s||'all'} onClick={() => setFiltroSource(s)}
              style={{ padding:'6px 14px', borderRadius:8, border:'1px solid', fontSize:12, fontWeight:600, cursor:'pointer',
                borderColor: filtroSource===s ? (cfg?.color||'var(--cyan)') : 'var(--border)',
                background: filtroSource===s ? (cfg?.color||'var(--cyan)')+'20' : 'var(--surface)',
                color: filtroSource===s ? (cfg?.color||'var(--cyan)') : 'var(--text-muted)' }}>
              {s ? cfgOf(s).label : 'Tutti'} {s ? `(${posts.filter(p=>p._src===s).length})` : `(${posts.length})`}
            </button>
          );
        })}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {sorted.map((p, i) => {
          const cfg = cfgOf(p._src);
          const reach = Number(p.metrics?.reach || p.metrics?.impressions || p.metrics?.views || p.metrics?.video_views || 0);
          const likes = Number(p.metrics?.likes || 0);
          const comments = Number(p.metrics?.comments || 0);
          const maxReach = Number(sorted[0]?.metrics?.reach || sorted[0]?.metrics?.impressions || sorted[0]?.metrics?.views || sorted[0]?.metrics?.video_views || 1);
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px' }}>
              <div style={{ width:32, height:32, borderRadius:8, flexShrink:0, background:`${cfg.color}20`, color:cfg.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800 }}>#{i+1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:10.5, color:cfg.color, fontWeight:700 }}>{cfg.label}</span>
                  <span style={{ fontSize:11, color:'var(--text-dim)' }}>{p.snapshot_date}</span>
                </div>
                <div style={{ fontSize:12.5, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:4 }}>{p.entity_name || p.entity_id}</div>
                <div style={{ display:'flex', gap:14, fontSize:11, color:'var(--text-muted)', flexWrap:'wrap' }}>
                  {reach > 0 && <span>👁 {fK(reach)}</span>}
                  {likes > 0 && <span>❤️ {fK(likes)}</span>}
                  {comments > 0 && <span>💬 {fInt(comments)}</span>}
                </div>
                <div style={{ height:3, borderRadius:2, background:'var(--border)', marginTop:6, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(reach/maxReach)*100}%`, background:cfg.color, borderRadius:2 }}/>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── CONFRONTO TAB ────────────────────────────────────────────────────── */
function ConfrontoTab({ account, channels, days, compareDays, setCompareDays, cfgOf, fInt }) {
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState('');

  const socialChannels = channels.filter(c => c.source !== 'ads');
  const source = selectedSource || socialChannels[0]?.source;
  const cfg = source ? cfgOf(source) : null;

  const load = useCallback(() => {
    if (!account || !source) return;
    setLoading(true);
    api.get('/meta_analytics/compare', { params: { account_key: account, source, level:'profile', days_current: days, days_compare: compareDays } })
      .then(r => { setCompareData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [account, source, days, compareDays]);

  useEffect(() => { load(); }, [load]);

  const current = compareData?.current || [];
  const compare = compareData?.compare || [];

  const getMetric = (arr, field) => arr.reduce((s,r) => s + (Number(r.metrics?.[field]||r[field])||0), 0);
  const delta = (a, b) => b === 0 ? 0 : ((a - b) / b * 100).toFixed(1);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {socialChannels.map(c => {
            const cfg2 = cfgOf(c.source);
            const sel = c.source === source;
            return (
              <button key={c.source} onClick={() => setSelectedSource(c.source)}
                style={{ padding:'7px 14px', borderRadius:9, border:`1px solid ${sel?cfg2.color:'var(--border)'}`, background: sel?`${cfg2.color}20`:'var(--surface)', color: sel?cfg2.color:'var(--text-muted)', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                {cfg2.label}
              </button>
            );
          })}
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11.5, color:'var(--text-dim)' }}>Confronta con:</span>
          <select value={compareDays} onChange={e => setCompareDays(Number(e.target.value))}
            className="eden-select" style={{ fontSize:12, padding:'5px 10px' }}>
            {[7,14,30,60,90].map(d => <option key={d} value={d}>{d} giorni fa</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)' }}>Caricamento confronto…</div>}
      {!loading && cfg && current.length > 0 && (() => {
        const fields = (cfgOf(source).kpis||[]).filter(k=>k[3]!=='val').map(k => ({ field:k[0], label:k[1] }));
        return (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:12 }}>
            {fields.map(({ field, label }) => {
              const curr = getMetric(current, field);
              const prev = getMetric(compare, field);
              const d = delta(curr, prev);
              const up = Number(d) >= 0;
              return (
                <div key={field} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
                  <div style={{ fontSize:11, color:'var(--text-dim)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8 }}>{label}</div>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:10, justifyContent:'space-between' }}>
                    <div>
                      <div style={{ fontSize:22, fontWeight:800, color:'var(--text)' }}>{fInt(curr)}</div>
                      <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>Ultimi {days}g</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:14, fontWeight:700, color: up?'var(--green)':'var(--red)' }}>{up?'+':''}{d}%</div>
                      <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>{fInt(prev)} ({compareDays}g fa)</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
      {!loading && (!compareData || current.length === 0) && (
        <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
          Servono almeno {days + compareDays} giorni di dati per il confronto.
        </div>
      )}
    </div>
  );
}

/* ── GOALS TAB ────────────────────────────────────────────────────────── */
function GoalsTab({ account, goals, setGoals, channels, cfgOf }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ source:'', metric_key:'', metric_label:'', target_value:'', periodo_tipo:'mensile' });
  const [saving, setSaving] = useState(false);

  const socialChannels = channels.filter(c => c.source !== 'ads');
  const srcKpis = form.source ? (cfgOf(form.source).kpis || []).filter(k=>k[3]!=='val') : [];

  const add = async (e) => {
    e.preventDefault();
    if (!form.source || !form.metric_key || !form.target_value) return;
    setSaving(true);
    try {
      const r = await api.post('/analytics_goals/', { ...form, cliente_id: account, target_value: Number(form.target_value) });
      setGoals(prev => [...prev, r.data]);
      setForm({ source:'', metric_key:'', metric_label:'', target_value:'', periodo_tipo:'mensile' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  const del = async (id) => {
    await api.delete(`/analytics_goals/${id}`);
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>Goals / Target KPI</div>
          <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:2 }}>Imposta target mensili per i tuoi KPI di analytics</div>
        </div>
        <button onClick={() => setShowForm(v=>!v)} className="eden-btn-primary" style={{ fontSize:12 }}>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}>+ Nuovo goal</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} style={{ background:'var(--bg3)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:10, padding:'14px 16px', display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:4, fontWeight:600 }}>Piattaforma</div>
              <select value={form.source} onChange={e => setForm(f=>({...f, source:e.target.value, metric_key:'', metric_label:''}))} className="eden-select" required>
                <option value="">Seleziona...</option>
                {socialChannels.map(c => <option key={c.source} value={c.source}>{cfgOf(c.source).label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:4, fontWeight:600 }}>Metrica</div>
              <select value={form.metric_key} onChange={e => { const kpi = srcKpis.find(k=>k[0]===e.target.value); setForm(f=>({...f, metric_key:e.target.value, metric_label:kpi?.[1]||e.target.value})); }} className="eden-select" required disabled={!form.source}>
                <option value="">Seleziona...</option>
                {srcKpis.map(k => <option key={k[0]} value={k[0]}>{k[1]}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:4, fontWeight:600 }}>Target</div>
              <input type="number" value={form.target_value} onChange={e=>setForm(f=>({...f,target_value:e.target.value}))} placeholder="es. 5000" className="eden-input" required style={{ fontSize:13 }}/>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:4, fontWeight:600 }}>Periodo</div>
              <select value={form.periodo_tipo} onChange={e=>setForm(f=>({...f,periodo_tipo:e.target.value}))} className="eden-select">
                <option value="mensile">Mensile</option>
                <option value="trimestrale">Trimestrale</option>
                <option value="annuale">Annuale</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button type="submit" disabled={saving} className="eden-btn-primary" style={{ fontSize:12 }}>{saving ? 'Salvo…' : 'Salva goal'}</button>
            <button type="button" onClick={() => setShowForm(false)} style={{ border:'1px solid var(--border)', borderRadius:7, background:'none', color:'var(--text-muted)', cursor:'pointer', padding:'6px 12px', fontSize:12 }}>Annulla</button>
          </div>
        </form>
      )}

      {goals.length === 0 ? (
        <div style={{ textAlign:'center', padding:'50px 0', color:'var(--text-dim)', fontSize:13 }}>
          Nessun goal impostato. Crea il primo target per monitorare i progressi.
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px,1fr))', gap:12 }}>
          {goals.map(g => {
            const cfg2 = cfgOf(g.source);
            return (
              <div key={g.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px', position:'relative' }}>
                <button onClick={() => del(g.id)} style={{ position:'absolute', top:10, right:10, background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', display:'flex', padding:4 }}
                  onMouseEnter={e=>e.currentTarget.style.color='var(--red)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                  ✕
                </button>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                  <span style={{ fontSize:11, color:cfg2.color, fontWeight:700 }}>{cfg2.label}</span>
                  <span style={{ fontSize:10.5, color:'var(--text-dim)', background:'var(--bg3)', padding:'1px 7px', borderRadius:20 }}>{g.periodo_tipo}</span>
                </div>
                <div style={{ fontSize:12, color:'var(--text-dim)', marginBottom:4 }}>{g.metric_label || g.metric_key}</div>
                <div style={{ fontSize:24, fontWeight:800, color:'var(--text)' }}>{new Intl.NumberFormat('it-IT').format(g.target_value)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
